// ExamTypes.tsx

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Typography,
  Chip,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import {
  GridColumns,
  GridRowId,
  GridRowParams,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'

import { DataTable } from '@components/tables'
import { Button } from '@components/buttons'

import { ExamType, Site } from '@services/api'
import { Actions } from '@state/actions'
import { useSelector } from '@state/store'
import { removeSites } from '@state/reducers/siteReducer'
import { deleteType, findAllType } from '@state/thunks/procedureThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { enqueueSnackbar } from '@state/reducers/alertReducer'

import { ExamTypeForm } from './ExamTypeForm'
import { EditIcon } from 'lucide-react'

interface DeleteDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  count: number
}

const DeleteDialog = ({
  open,
  onClose,
  onConfirm,
  count,
}: DeleteDialogProps) => {
  const intl = useIntl()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: 400,
          p: 1,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, pr: 1 }}>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {intl.formatMessage({ id: 'deleteConfirmation' })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {count} type(s) sélectionné(s)
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          text={intl.formatMessage({ id: 'Cancel' })}
        />
        <Button
          fullWidth
          color="error"
          textColor="white"
          onClick={onConfirm}
          text={intl.formatMessage({ id: 'delete' })}
        />
      </DialogActions>
    </Dialog>
  )
}

interface ExamTypeDialogProps {
  open: boolean
  onClose: () => void
  examType?: ExamType | null
}

const ExamTypeDialog = ({ open, onClose, examType }: ExamTypeDialogProps) => {
  const intl = useIntl()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: 500,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {examType
            ? intl.formatMessage({ id: 'updateType' })
            : intl.formatMessage({ id: 'addType' })}{' '}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 1 }}>
        <ExamTypeForm row={examType || undefined} onClose={onClose} />
      </DialogContent>
    </Dialog>
  )
}

export default function ExamTypes() {
  const dispatch = useDispatch()
  const intl = useIntl()

  // State
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [examTypeToEdit, setExamTypeToEdit] = useState<ExamType | null>(null)

  // Selectors
  const { loading, examTypes } = useSelector(({ examType, loading }) => ({
    examTypes: examType.examTypes,
    loading:
      loading[Actions.PROCEDURE_TYPE_FIND_ALL] ||
      loading[Actions.PROCEDURE_TYPE_CREATE] ||
      loading[Actions.PROCEDURE_TYPE_UPDATE],
  }))

  // Handlers
  const onRefresh = useCallback(() => {
    dispatch(findAllType())
    dispatch(findAllSites())
  }, [dispatch])

  useEffect(() => {
    onRefresh()
    return () => {
      dispatch(removeSites())
    }
  }, [dispatch, onRefresh])

  const handleOpenDialog = (examType?: ExamType) => {
    setExamTypeToEdit(examType || null)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setExamTypeToEdit(null)
  }

  const handleDeleteConfirm = () => {
    dispatch(deleteType(selectedRows))
    setDeleteDialogOpen(false)
    setSelectedRows([])
  }

  const handleDeleteClick = () => {
    if (selectedRows.length > 0) {
      setDeleteDialogOpen(true)
    } else {
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'noTypeSelected' }),
          type: 'error',
        }),
      )
    }
  }

  const handleRowDoubleClick = useCallback(
    ({ row }: GridRowParams<ExamType>) => {
      handleOpenDialog(row)
    },
    [],
  )

  // Columns
  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'code' }),
        flex: 1,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'label',
        headerName: intl.formatMessage({ id: 'label' }),
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'site',
        headerName: intl.formatMessage({ id: 'site' }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const site = params.value as Site | undefined
          return site?.label ? (
            <Chip
              label={site.label}
              size="small"
              variant="outlined"
              color="primary"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              handleOpenDialog(params.row as ExamType)
            }}
            sx={{
              color: 'primary.main',
              '&:hover': { backgroundColor: 'primary.lighter' },
            }}
          >
            <EditIcon fontSize="small" color="info" />
          </IconButton>
        ),
      },
    ],
    [intl],
  )

  return (
    <Box sx={{ height: '95%', display: 'flex', flexDirection: 'column' }}>
      {/* Data Table */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataTable
          rows={examTypes}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'examTypes',
            icon: 'fa fa-syringe',
            onAdd: () => handleOpenDialog(),
            onRefresh,
            onDelete: handleDeleteClick,
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={(selection: GridRowId[]) =>
            setSelectedRows(selection as number[])
          }
          onRowDoubleClick={handleRowDoubleClick}
        />
      </Paper>

      {/* Dialogs */}
      <ExamTypeDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        examType={examTypeToEdit}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedRows.length}
      />
    </Box>
  )
}
